import { useState, type VoidFunctionComponent } from 'react';

import { isCustomLicense } from '../../../../../shared/licenses';
import Locator from '../../../../../shared/Locator';
import { Action } from '../../../../../typings/permissions';
import useCorrectLicense from '../../../../hooks/api/licenses/useCorrectLicense';
import useEditable from '../../../../hooks/useEditable';
import useHasProjectPermission from '../../../../shared/permissions/useHasProjectPermission';
import Button from '../../../generic/Button/Button';
import { BUTTON_TYPE } from '../../../generic/Button/button-constants';
import ButtonGroup from '../../../generic/Button/ButtonGroup';
import Flex from '../../../generic/Flex';
import TextInput from '../../../generic/Forms/TextInput';
import type { LicenseResponse } from '../../../../hooks/api/licenses/useLicense';
import type { DependencyProp } from '../types';

import styles from '../LicenseDrawer.module.less';

type GlobalScopeProps = {
  license: LicenseResponse;
  dependency?: never;
  revisionLicenseId?: never;
};

type DependencyScopeProps = {
  license: LicenseResponse;
  dependency: DependencyProp;
  revisionLicenseId: number;
};

type LicenseTitleProps = GlobalScopeProps | DependencyScopeProps;

type LicenseTitleEditingProps = DependencyScopeProps & { setIsEditing: (isEditing: boolean) => void };

const LicenseTitleEditing: VoidFunctionComponent<LicenseTitleEditingProps> = ({
  license,
  dependency,
  revisionLicenseId,
  setIsEditing,
}) => {
  const { data: titleData, isDirty, onUpdate, onSave } = useEditable(license.name ?? '');
  const { mutate: correctLicense } = useCorrectLicense(dependency.locator, revisionLicenseId);
  return (
    <div className={styles.title}>
      <TextInput label="License Title" value={titleData} onChange={onUpdate} />
      <Flex justify="space-between" direction="row-reverse">
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              correctLicense({ data: { title: titleData } });
              onSave();
              setIsEditing(false);
            }}
            isDisabled={!isDirty}
          >
            Save
          </Button>
        </ButtonGroup>
      </Flex>
    </div>
  );
};

const LicenseTitle: VoidFunctionComponent<LicenseTitleProps> = ({ license, dependency, revisionLicenseId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const hasPermission = useHasProjectPermission(Locator.parsePackageString(dependency?.locator ?? ''), Action.Edit);
  const canEditLicenseTitle =
    isCustomLicense(license.licenseId) && hasPermission;
  return (
    <div className={styles.title}>
      {isEditing && dependency && revisionLicenseId ? (
        <LicenseTitleEditing
          license={license}
          dependency={dependency}
          revisionLicenseId={revisionLicenseId}
          setIsEditing={setIsEditing}
        />
      ) : (
        <>
          {license.name}
          {dependency && (
            <Flex justify="space-between">
              <div className={styles.subtitle}>
                in <span className={styles.dependencyName}>{dependency.title}</span>
                <span className={styles.dependencyVersion}>
                  {dependency.version ?? Locator.parse(dependency.locator).revision}
                </span>
              </div>
              {canEditLicenseTitle && (
                <Button type={BUTTON_TYPE.SECONDARY} onClick={() => setIsEditing(true)}>
                  Edit License Title
                </Button>
              )}
            </Flex>
          )}
        </>
      )}
    </div>
  );
};

export default LicenseTitle;
